import type { FormEvent } from 'react'
import type { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import Head from 'next/head'
import { useState } from 'react'
import { useRouter } from 'next/router'

import { Logo } from '@/components/ui/Logo'
import { SlimLayout } from '@/components/layout/SlimLayout'
import {
  AuthDivider,
  AuthHeader,
  AuthLegalFooter,
  AuthPrimaryButton,
  GoogleButton,
  authInputClasses,
} from '@/components/features/auth/AuthUi'
import { signIn, type ClientSafeProvider } from "next-auth/react"
import { getServerSession } from "next-auth/next"
import { authOptions } from "../api/auth/[...nextauth]";

type LoginProps = {
  providers: Record<string, ClientSafeProvider>;
};

export default function Login({ providers }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()

  const callbackUrl =
    typeof router.query?.callbackUrl === 'string' ? router.query.callbackUrl : '/'
  const accountDisabled = router.query?.error === 'AccountDisabled'

  const googleProvider = Object.values(providers ?? {}).find(
    (provider) => provider.name === 'Google',
  )

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await signIn('email', { email, callbackUrl })
    } catch {
      // signIn navigates away; an in-flight fetch aborted by that navigation
      // rejects with a network TypeError ("Load failed"). Nothing to handle.
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <SlimLayout>
      <Head>
        <title>Sign In - ReceiptsAI</title>
      </Head>
      <div className="flex">
          <Logo className="h-10 w-auto" />
      </div>
      <AuthHeader
        title="Welcome back"
        subtitle="Sign in or create an account — no password needed."
      />

      <div className="mt-8 space-y-6">
        {accountDisabled && (
          <div role="alert" className="rounded-md bg-red-50 p-4 text-sm text-red-800">
            This account has been disabled. If you think this is a mistake, contact
            support@receiptsai.com.
          </div>
        )}
        {googleProvider && (
          <GoogleButton
            onClick={() => {
              // Swallow the abort rejection when the OAuth redirect interrupts
              // signIn's own fetches (Sentry "TypeError: Load failed" noise).
              void signIn(googleProvider.id, { callbackUrl }).catch(() => {})
            }}
          />
        )}

        <AuthDivider>or continue with email</AuthDivider>

        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <input
            type="email"
            name="email"
            autoComplete="email"
            required
            placeholder="you@company.com"
            aria-label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={authInputClasses}
          />
          <AuthPrimaryButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending magic link…' : 'Continue with email'}
          </AuthPrimaryButton>
          <p className="text-xs leading-5 text-gray-500">
            We&rsquo;ll email you a magic link for password-free sign in.
          </p>
        </form>
      </div>

      <AuthLegalFooter />
    </SlimLayout>
  )
}

export const getServerSideProps: GetServerSideProps<LoginProps> = async (context) => {
  const session = await getServerSession(context.req, context.res, authOptions);
  if (session) {
    // Honor a safe (same-origin, relative) callbackUrl so deep links like the
    // workspace-invite accept page survive an already-authenticated visit.
    // Reject protocol-relative (`//host`) and absolute URLs to avoid open redirects.
    const raw = context.query?.callbackUrl;
    const cb = typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
    return { redirect: { destination: cb, permanent: false } };
  }
  // Build the provider list locally from the auth config. next-auth/react's
  // getProviders() HTTP-fetches /api/auth/providers from the server itself,
  // which fails when the site sits behind Cloudflare Access (the server-side
  // fetch has no Access cookie → 302 → login page silently drops the Google
  // button). authOptions is already imported for getServerSession above.
  const providers: Record<string, ClientSafeProvider> = {};
  for (const p of authOptions.providers ?? []) {
    providers[p.id] = {
      id: p.id,
      name: p.name,
      type: p.type,
      // Mirror the defaults next-auth's own getProviders() injects (v4):
      signinUrl: `/api/auth/signin/${p.id}`,
      callbackUrl: `/api/auth/callback/${p.id}`,
    };
  }

  return {
    props: { providers },
  }
}
